import Button from '@mui/material/Button';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { Toast } from '#components/toast/toast.js';
import { useAuth0Context } from '#state/contexts/auth0-context.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { LoginDenialTone } from './login-denial-tone.js';

import type { ReactNode } from 'react';
import type { Auth0AppState } from '#utils/auth0-app-state.js';

const ICON_TILE_CLASS_NAMES: Record<LoginDenialTone, string> = {
	[LoginDenialTone.Info]: 'border-blue-a5 bg-blue-a3 text-blue-11',
	[LoginDenialTone.Warning]: 'border-amber-a6 bg-amber-a3 text-amber-11',
};

export interface LoginDenialScreenProps {
	/** Icon shown in the tile beside the title. */
	icon: ReactNode;
	/** Colours the icon tile. */
	tone: LoginDenialTone;
	title: string;
	message: string;
	/** Label of the primary action, which starts a fresh login. */
	loginLabel: string;
	/** Forces Auth0 to ask for credentials rather than reusing the session that was just denied. */
	forceReauthentication?: boolean;
	/** Extra actions or notes rendered under the login button. */
	children?: ReactNode;
}

/**
 * Landing screen for a login the Post-Login gate denied: an explanation and a
 * primary action that starts a fresh login. Failure to start the login surfaces as
 * an error toast.
 *
 * @param props - See {@link LoginDenialScreenProps}.
 * @returns The screen.
 */
export function LoginDenialScreen({
	icon,
	tone,
	title,
	message,
	loginLabel,
	forceReauthentication = false,
	children,
}: LoginDenialScreenProps) {
	const el = useDataElTagGenerator(LoginDenialScreen);
	const auth0 = useAuth0Context();
	const addToast = useAddToast();

	const [isLoading, setIsLoading] = useState(false);

	const handleLogIn = useCallback(() => {
		setIsLoading(true);

		void auth0.client
			.loginWithRedirect<Auth0AppState>({
				appState: { returnTo: '/' },
				...(forceReauthentication && { authorizationParams: { prompt: 'login' } }),
			})
			.catch((error: unknown) => {
				setIsLoading(false);
				addToast(
					<Toast severity='error' title='Login failed'>
						{error instanceof Error ? error.message : 'Failed to log in.'}
					</Toast>,
				);
			});
	}, [addToast, auth0.client, forceReauthentication]);

	return (
		<div {...el()} className='grid min-h-0 min-w-0 grow place-items-center p-6'>
			<div
				{...el('container')}
				className='flex w-full max-w-sm flex-col items-center gap-6 rounded-xl border border-gray-6 bg-gray-2 p-6 text-center'
			>
				<div className='flex flex-col items-center gap-4'>
					<div className='flex items-center justify-center gap-3.5'>
						<div
							className={clsx(
								'flex size-10 flex-none items-center justify-center rounded-lg border',
								ICON_TILE_CLASS_NAMES[tone],
							)}
						>
							{icon}
						</div>
						<h1 className='text-lg font-semibold text-gray-12'>{title}</h1>
					</div>
					<p className='text-pretty text-gray-11'>{message}</p>
				</div>

				<div className='flex w-full flex-col items-center gap-3'>
					<Button loading={isLoading} onClick={handleLogIn} className='w-full'>
						<span>{loginLabel}</span>
					</Button>
					{children}
				</div>
			</div>
		</div>
	);
}
